import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  name: "", email: "", password: "", designation: "", date_of_joining: "",
  sow: "", reporting_manager: "", phone: "", address: "", dob: "",
};

export default function EmployeeFormDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/employees", form);
      toast.success("Employee added to roster");
      if (res.data.email_sent === false) {
        toast.warning("Note: welcome email with login details could not be delivered. Please share credentials with the employee manually.");
      }
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="employee-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Employee</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Full Name</Label>
              <Input data-testid="employee-form-name-input" value={form.name} onChange={set("name")} placeholder="e.g. Nitin Oli" />
            </div>
            <div className="space-y-1.5">
              <Label>Designation</Label>
              <Input data-testid="employee-form-designation-input" value={form.designation} onChange={set("designation")} placeholder="e.g. Sustainability Consultant" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Login Email</Label>
              <Input data-testid="employee-form-email-input" type="email" value={form.email} onChange={set("email")} placeholder="employee@envirowealth.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Login Password</Label>
              <Input data-testid="employee-form-password-input" type="text" value={form.password} onChange={set("password")} placeholder="Set initial password" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Date of Joining</Label>
              <Input data-testid="employee-form-doj-input" type="date" value={form.date_of_joining} onChange={set("date_of_joining")} />
            </div>
            <div className="space-y-1.5">
              <Label>Reporting Manager / POC</Label>
              <Input data-testid="employee-form-manager-input" value={form.reporting_manager} onChange={set("reporting_manager")} placeholder="e.g. Mishra Om" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Scope of Work (SOW)</Label>
            <Input data-testid="employee-form-sow-input" value={form.sow} onChange={set("sow")} placeholder="Describe core responsibilities" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input data-testid="employee-form-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 XXXXX XXXXX" />
            </div>
            <div className="space-y-1.5">
              <Label>Address</Label>
              <Input data-testid="employee-form-address-input" value={form.address} onChange={set("address")} placeholder="City, State" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Date of Birth</Label>
            <Input data-testid="employee-form-dob-input" type="date" value={form.dob} onChange={set("dob")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="employee-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="employee-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Adding..." : "Add Employee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
